import React from "react";
import styled from "styled-components";
import { Container, Section } from "../components/ui/Container";
import { PageHeader } from "../components/ui/PageHeader";
import { colors, fonts, media } from "../theme";

const content = {
  terms: {
    eyebrow: "Legal",
    title: "Terms of Service",
    sub: "These terms govern your use of the Everpesa platform, including invest.everpesa.com and any product operated by Everpesa Technologies Ltd.",
    body: [
      ["1. About Everpesa", "Everpesa Technologies Ltd is a company incorporated in Uganda and licensed by the Uganda Microfinance Regulatory Authority as a Tier 4 money lender. Everpesa operates a peer to peer lending marketplace and related fintech products."],
      ["2. Eligibility", "You must be at least 18 years old, a resident of Uganda or of a country we explicitly serve, and able to enter into a binding contract under the laws of Uganda."],
      ["3. Investor terms", "Investors fund borrower loans through the platform. Returns are net of platform fees and reserve contributions. Principal protection applies subject to the terms disclosed on each loan listing."],
      ["4. Borrower terms", "Borrowers agree to repay the principal and interest on the agreed schedule. Late payment fees may apply. Defaults are reported to relevant credit bureaus where applicable."],
      ["5. KYC and AML", "We perform identity, sanctions and PEP checks in line with Bank of Uganda and FIA regulations. Suspicious activity will be reported to the relevant authorities."],
      ["6. Limitation of liability", "To the maximum extent permitted by law, Everpesa is not liable for indirect or consequential losses. Investments carry risk and past performance does not guarantee future results."],
      ["7. Changes", "We may update these terms from time to time. Material changes will be notified through the platform or by email."],
    ],
  },
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    sub: "How Everpesa collects, uses and protects your personal data, under Uganda's Data Protection and Privacy Act, 2019.",
    body: [
      ["1. What we collect", "Identity data such as name, ID and photo, contact data such as phone, email and address, financial data such as mobile money statements, transactions and repayments, device data, and location data where you grant permission."],
      ["2. Why we collect it", "To verify your identity, score your application, disburse and recover loans, comply with anti money laundering rules, and improve the platform."],
      ["3. Who we share it with", "Service providers that support our platform such as cloud hosting, KYC and payments, regulators where required, and credit bureaus where applicable. We do not sell your data."],
      ["4. How long we keep it", "For as long as you have an active account and as required by Ugandan law, including statutory record keeping after account closure."],
      ["5. Your rights", "You can access, correct or request deletion of your personal data, subject to lawful exceptions. Contact info@everpesa.com to exercise these rights."],
      ["6. Security", "We use encryption in transit and at rest, role based access, and audit logging. No system is 100% secure, so please report suspected breaches to info@everpesa.com immediately."],
    ],
  },
  compliance: {
    eyebrow: "Legal",
    title: "Compliance and licensing",
    sub: "Everpesa is licensed and regulated. We take consumer protection, anti money laundering and data privacy seriously.",
    body: [
      ["Money lending licence", "Everpesa Technologies Ltd holds a Tier 4 money lending licence under the Uganda Microfinance Regulatory Authority. A copy of our licence is available on request."],
      ["AML and CFT", "We follow the Anti Money Laundering Act, 2013 and the Financial Intelligence Authority reporting framework. We perform identity verification, sanctions screening and ongoing monitoring."],
      ["Data protection", "We are registered under Uganda's Data Protection and Privacy Act, 2019. Our data protection officer is reachable at info@everpesa.com."],
      ["Anti fraud and ethics", "Our anti corruption and fraud policy is reviewed annually. Suspected fraud, corruption or unethical behaviour can be reported confidentially to info@everpesa.com."],
      ["Consumer protection", "Borrower interest rates, fees and total cost of credit are disclosed before any loan is signed. Borrowers retain the right to early repayment without penalty."],
    ],
  },
};

const Article = ({ doc }) => (
  <>
    <PageHeader eyebrow={doc.eyebrow} title={doc.title} sub={doc.sub} />
    <Section>
      <Container>
        <Body>
          {doc.body.map(([h, p]) => (
            <Block key={h}>
              <h2>{h}</h2>
              <p>{p}</p>
            </Block>
          ))}
        </Body>
      </Container>
    </Section>
  </>
);

export const Terms = () => <Article doc={content.terms} />;
export const Privacy = () => <Article doc={content.privacy} />;
export const Compliance = () => <Article doc={content.compliance} />;

const Body = styled.div`
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Block = styled.div`
  h2 {
    font-family: ${fonts.display};
    font-size: 22px;
    margin-bottom: 10px;
    color: ${colors.ink};
  }
  p {
    font-size: 16px;
    line-height: 1.7;
  }
  ${media.sm} { h2 { font-size: 19px; } }
`;
